
import { supabase } from "@/integrations/supabase/client";

interface TaskData {
  execution_window?: {
    deadline?: string;
  };
  resource_requirements?: {
    priority?: number;
  };
  tasks: {
    dependencies?: string[];
    status: string;
  };
}

export class TaskPrioritizationManager {
  async calculatePriority(taskId: string): Promise<number> {
    const { data: task } = await supabase
      .from('task_scheduling')
      .select(`
        *,
        tasks (
          id,
          dependencies,
          status
        )
      `)
      .eq('task_id', taskId)
      .single();

    if (!task) return 1.0;

    const taskData = task as unknown as TaskData;
    let priorityScore = 1.0;

    // Factor in deadline proximity
    if (taskData.execution_window?.deadline) {
      const deadline = new Date(taskData.execution_window.deadline);
      const timeUntilDeadline = deadline.getTime() - Date.now();
      if (timeUntilDeadline <= 0) {
        priorityScore *= 2.0; // Overdue tasks get double priority
      } else {
        const hoursUntilDeadline = timeUntilDeadline / (1000 * 60 * 60);
        if (hoursUntilDeadline < 24) {
          priorityScore *= 1.5; // Urgent tasks (< 24h) get 1.5x priority
        }
      }
    }

    // Factor in resource requirements
    if (taskData.resource_requirements?.priority) {
      priorityScore *= taskData.resource_requirements.priority;
    }

    // Factor in dependencies
    const dependencies = taskData.tasks.dependencies;
    if (dependencies && dependencies.length > 0) {
      const dependencyScore = await this.calculateDependencyScore(dependencies);
      priorityScore *= dependencyScore;
    }

    return priorityScore;
  }

  private async calculateDependencyScore(dependencies: string[]): Promise<number> {
    const { data: dependentTasks } = await supabase
      .from('tasks')
      .select('status')
      .in('id', dependencies);

    if (!dependentTasks?.length) return 1.0;

    const completedDependencies = dependentTasks.filter(t => t.status === 'completed').length;
    return 1.0 + (completedDependencies / dependentTasks.length);
  }

  async updateTaskPriorities(): Promise<void> {
    const { data: tasks } = await supabase
      .from('task_scheduling')
      .select('task_id');

    if (!tasks) return;

    for (const task of tasks) {
      const priority = await this.calculatePriority(task.task_id);
      await supabase
        .from('task_scheduling')
        .update({ priority_score: priority })
        .eq('task_id', task.task_id);
    }
  }
}
